/**
 * IconButton — a square, icon-only button (× closes, inline row actions).
 *
 * Replaces the per-page copy-paste of `{ background: 'none'|'transparent',
 * border: 'none', color: 'var(--text-muted)', fontSize, cursor: 'pointer' }`
 * on a raw `<button>` holding a single glyph. Icon-only, so `aria-label`
 * is **required** — the button has no text for a screen reader otherwise.
 *
 * Adds a subtle hover fill (`--bg-tertiary`) the hand-rolled versions
 * mostly lacked — a deliberate, minor affordance improvement, consistent
 * across every icon button now that they share one component.
 */

import { forwardRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonTone = 'muted' | 'secondary' | 'danger' | 'accent';

const SIZE: Record<IconButtonSize, { box: number; fontSize: number }> = {
  sm: { box: 24, fontSize: 16 },
  md: { box: 28, fontSize: 18 },
  lg: { box: 32, fontSize: 20 },
};

const TONE_VAR: Record<IconButtonTone, string> = {
  muted: 'var(--text-muted)',
  secondary: 'var(--text-secondary)',
  danger: 'var(--red)',
  accent: 'var(--accent)',
};

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — an icon-only button has no text, so it needs an accessible name. */
  'aria-label': string;
  size?: IconButtonSize;
  tone?: IconButtonTone;
  /**
   * Solid fill: paints the `tone` color as the background with a white glyph
   * (e.g. an accent "send" button). When `disabled`, a filled button reads as
   * inactive (`--bg-tertiary` fill + `--text-muted` glyph) rather than dimming.
   * Default `false` — a chrome-less glyph that fills on hover.
   */
  filled?: boolean;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { size = 'md', tone = 'muted', filled = false, type = 'button', disabled, style, children, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  const { box, fontSize } = SIZE[size];
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: box,
        height: box,
        flexShrink: 0,
        borderRadius: 8,
        border: 'none',
        background: filled
          ? disabled
            ? 'var(--bg-tertiary)'
            : TONE_VAR[tone]
          : hover && !disabled
            ? 'var(--bg-tertiary)'
            : 'transparent',
        color: filled ? (disabled ? 'var(--text-muted)' : 'white') : TONE_VAR[tone],
        cursor: disabled ? (filled ? 'not-allowed' : 'default') : 'pointer',
        fontSize,
        lineHeight: 1,
        padding: 0,
        opacity: !filled && disabled ? 0.5 : 1,
        transition: 'background 0.15s ease',
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
});
